import React from 'react';
import { Star, ExternalLink, ThumbsUp, MessageCircle, Award } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { testimonials, contactInfo } from '../data/mock';

const Reviews = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 via-white to-amber-50 py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="inline-flex items-center space-x-2 bg-brand-gold/20 px-4 py-2 rounded-full border border-brand-gold/30 mb-6">
            <Star className="h-5 w-5 text-brand-gold fill-current" />
            <span className="text-sm font-semibold text-brand-navy">Customer Reviews</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-brand-navy mb-6">
            What Our Clients Say
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Read reviews from our satisfied customers across Winnipeg, Manitoba
          </p>
          
          {/* Google Review CTA */}
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-2xl mx-auto border-2 border-brand-gold/30">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <div className="flex text-brand-gold">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} className="h-8 w-8 fill-current" />
                ))}
              </div>
            </div>
            <h2 className="text-2xl font-bold text-brand-navy mb-3">
              Love Our Service?
            </h2>
            <p className="text-gray-600 mb-6">
              Your feedback helps us improve and helps others find quality property care services in Winnipeg!
            </p>
            <a
              href={contactInfo.googleReviewLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block"
            >
              <Button size="lg" className="bg-brand-navy hover:bg-brand-navy-dark text-white text-lg px-8 py-6 shadow-xl hover:shadow-2xl transition-all duration-300">
                <Star className="h-5 w-5 mr-2 fill-current" />
                Leave a Google Review
                <ExternalLink className="h-5 w-5 ml-2" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { icon: ThumbsUp, number: '500+', label: 'Happy Clients' },
              { icon: Star, number: '5.0', label: 'Average Rating' },
              { icon: MessageCircle, number: '200+', label: 'Reviews' },
              { icon: Award, number: '100%', label: 'Satisfaction Rate' }
            ].map((stat, idx) => (
              <div key={idx} className="text-center space-y-3">
                <div className="w-16 h-16 bg-brand-navy rounded-full flex items-center justify-center mx-auto">
                  <stat.icon className="h-8 w-8 text-brand-gold" />
                </div>
                <p className="text-3xl font-bold text-brand-navy">{stat.number}</p>
                <p className="text-gray-600">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-brand-navy mb-4">
              Customer Testimonials
            </h2>
            <p className="text-lg text-gray-600">
              Real feedback from real customers in Winnipeg
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-2 hover:border-brand-gold transition-all duration-300 hover:shadow-xl card-hover">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex text-brand-gold">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="h-5 w-5 fill-current" />
                      ))}
                    </div>
                    <span className="text-xs text-gray-500 bg-brand-blue/10 px-3 py-1 rounded-full">
                      Verified
                    </span>
                  </div>
                  <p className="text-gray-700 italic leading-relaxed">"{testimonial.text}"</p>
                  <div className="pt-4 border-t border-gray-200">
                    <p className="font-bold text-brand-navy">{testimonial.name}</p>
                    <p className="text-sm text-gray-600">{testimonial.location}</p>
                    <p className="text-xs text-brand-blue mt-1 font-medium bg-brand-blue/10 px-2 py-1 rounded inline-block">
                      {testimonial.service}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Additional placeholder reviews */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                name: "Robert Johnson",
                text: "Professional team! They handled our commercial space with great care and attention to detail. Highly recommended for any business owner.",
                service: "Commercial Cleaning",
                rating: 5
              },
              {
                name: "Emily Wilson",
                text: "The snow removal service is outstanding! They're always prompt, even during heavy snowfalls. Makes winter so much easier.",
                service: "Snow Removal",
                rating: 5
              }
            ].map((review, idx) => (
              <Card key={idx} className="border-2 hover:border-brand-gold transition-all duration-300 hover:shadow-xl">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex text-brand-gold">
                      {[...Array(review.rating)].map((_, i) => (
                        <Star key={i} className="h-5 w-5 fill-current" />
                      ))}
                    </div>
                    <span className="text-xs text-gray-500 bg-brand-blue/10 px-3 py-1 rounded-full">
                      Verified
                    </span>
                  </div>
                  <p className="text-gray-700 italic leading-relaxed">"{review.text}"</p>
                  <div className="pt-4 border-t border-gray-200">
                    <p className="font-bold text-brand-navy">{review.name}</p>
                    <p className="text-sm text-gray-600">Winnipeg, MB</p>
                    <p className="text-xs text-brand-blue mt-1 font-medium bg-brand-blue/10 px-2 py-1 rounded inline-block">
                      {review.service}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Review Us Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-brand-navy mb-6">
            Why Your Review Matters
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="space-y-3">
              <div className="w-12 h-12 bg-brand-gold/20 rounded-full flex items-center justify-center mx-auto">
                <ThumbsUp className="h-6 w-6 text-brand-gold" />
              </div>
              <h3 className="font-semibold text-brand-navy">Help Others</h3>
              <p className="text-sm text-gray-600">Your experience helps others make informed decisions</p>
            </div>
            <div className="space-y-3">
              <div className="w-12 h-12 bg-brand-gold/20 rounded-full flex items-center justify-center mx-auto">
                <Award className="h-6 w-6 text-brand-gold" />
              </div>
              <h3 className="font-semibold text-brand-navy">Support Local</h3>
              <p className="text-sm text-gray-600">Help a Winnipeg business grow and serve more customers</p>
            </div>
            <div className="space-y-3">
              <div className="w-12 h-12 bg-brand-gold/20 rounded-full flex items-center justify-center mx-auto">
                <Star className="h-6 w-6 text-brand-gold fill-current" />
              </div>
              <h3 className="font-semibold text-brand-navy">Improve Service</h3>
              <p className="text-sm text-gray-600">Your feedback helps us continually improve</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-brand-navy via-brand-navy-light to-brand-blue relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Share Your Experience
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            It only takes a minute and means the world to us!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={contactInfo.googleReviewLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button size="lg" className="bg-brand-gold hover:bg-brand-gold-dark text-brand-navy font-bold text-lg px-8 py-6 w-full sm:w-auto shadow-2xl">
                <Star className="h-5 w-5 mr-2 fill-current" />
                Write a Review
                <ExternalLink className="h-5 w-5 ml-2" />
              </Button>
            </a>
            <Link to="/book">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-brand-navy text-lg px-8 py-6 w-full sm:w-auto">
                Book Our Service
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Reviews;
